import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  createTheme,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useState } from "react";
import { formatDate } from "../../../common/const";
import { BuildingSelectionModal, BuildingType } from "./BuildingSelectionModal";
import RequiredLabel from "../../atoms/RequiredLabel";
import { SdcSnSelectionModal } from "./SdcSnSelectionModal";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { HaudiUuid } from "../../../utils/HaudiUUID";
import AutomaticallyCalculatedLabel from "../../atoms/AutomaticallyCalculatedLabel";
import SimOperationModal from "./SimOperationModal";
import ConfirmDialog from "../../molecules/ConfirmDialog";

const sdcTxPowerLevels = ["LOW", "MIDDLE", "HIGH"];

const theme = createTheme({
  spacing: [1, 4, 8, 12, 20, 32, 52, 84, 136, 220, 356],
});
const tableCellChildHeight = theme.spacing(6);
const tableCellChildMl = theme.spacing(4);
const tableCellChildTitleWidth = theme.spacing(8);
const tableCellChildTitleMr = theme.spacing(5);

type BuildingInSdcDetails = {
  autoBuildingId: number;
  buildingManagementId: string;
  buildingId: string;
  buildingName: string;
  buildingAddress: string;
};

export type SdcDetails = {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  shippingDate: string;
  firmwareVersion: string;
  wifiMacAddress: string;
  bluetoothDeviceAddress: string;
  isLteEnabled: boolean;
  lteApn: string;
  lteId: string;
  ltePassword: string;
  lteAuthType: string;
  isWifiEnabled: boolean;
  defaultWifi: number;
  ap1Ssid: string;
  ap1Password: string;
  ap1IpConfigType: string;
  ap1StaticIp: string | null;
  ap1StaticGateWay: string | null;
  ap1StaticDns: string | null;
  ap2Ssid: string;
  ap2Password: string;
  ap2IpConfigType: string;
  ap2StaticIp: string | null;
  ap2StaticGateWay: string | null;
  ap2StaticDns: string | null;
  ap3Ssid: string;
  ap3Password: string;
  ap3IpConfigType: string;
  ap3StaticIp: string | null;
  ap3StaticGateWay: string | null;
  ap3StaticDns: string | null;
  isBleEnabled: boolean;
  iBeaconMajor: string;
  iBeaconMinor: string;
  isAalEnabled: boolean;
  isElectronicLockEnabled: boolean;
  isNfcEnabled: boolean;
  isNfcCertRequired: boolean;
  isSciEnabled: boolean;
  isGuideBellEnabled: boolean;
  isSpeakerEnabled: boolean;
  isSignageEnabled: boolean;
  sciSignageIpAddr: string;
  idInCluster: number;
  isDefaultOpenHalf: boolean;
  isPushButtonEnabled: boolean;
  defaultDistanceNear: number;
  defaultDistanceFar: number;
  txPowerLevel: string;
  isSpeakerContactEnabled: boolean;
  isEnabled: boolean;
  doorPlace: string | null;
  installationDate: string;
  billingStartAt: string | null;
  memo: string;
  building: BuildingInSdcDetails | null;
  simId: string;
  simStatus: string;
  simOperation: string;
  logMode: string;
};

export type DoorDetailsValidationErrors = {
  doorPlace: string;
  memo: string;
  ap1IpConfigType: string;
  ap2IpConfigType: string;
  ap3IpConfigType: string;
  isLteEnabled: string;
  isWifiEnabled: string;
  ap1StaticIp: string;
  ap2StaticIp: string;
  ap3StaticIp: string;
  sciSignageIpAddr: string;

  // below are specific for registration
  sdcSerialNumber: string;
};

export const defaultSdc = {
  autoSdcId: 0,
  sdcUuid: "",
  sdcSerialNumber: "",
  shippingDate: "",
  firmwareVersion: "",
  wifiMacAddress: "",
  bluetoothDeviceAddress: "",
  isLteEnabled: false,
  lteApn: "",
  lteId: "",
  ltePassword: "",
  lteAuthType: "CHAP",
  isWifiEnabled: false,
  defaultWifi: 1,
  ap1Ssid: "",
  ap1Password: "",
  ap1IpConfigType: "DHCP",
  ap1StaticIp: "",
  ap1StaticGateWay: "",
  ap1StaticDns: "",
  ap2Ssid: "",
  ap2Password: "",
  ap2IpConfigType: "DHCP",
  ap2StaticIp: "",
  ap2StaticGateWay: "",
  ap2StaticDns: "",
  ap3Ssid: "",
  ap3Password: "",
  ap3IpConfigType: "DHCP",
  ap3StaticIp: "",
  ap3StaticGateWay: "",
  ap3StaticDns: "",
  isBleEnabled: false,
  iBeaconMajor: "",
  iBeaconMinor: "",
  isAalEnabled: false,
  isElectronicLockEnabled: false,
  isNfcEnabled: false,
  isNfcCertRequired: false,
  isSciEnabled: false,
  isGuideBellEnabled: false,
  isSpeakerEnabled: false,
  isSignageEnabled: false,
  sciSignageIpAddr: "239.255.68.61",
  idInCluster: 0,
  isDefaultOpenHalf: false,
  isPushButtonEnabled: false,
  defaultDistanceNear: 3,
  defaultDistanceFar: 8,
  txPowerLevel: "LOW",
  isSpeakerContactEnabled: false,
  isEnabled: false,
  doorPlace: "",
  installationDate: "",
  billingStartAt: "",
  memo: "",
  building: null,
  simId: "",
  simStatus: "",
  simOperation: "",
  logMode: "0", //通常
};

export const blankDoorDetailsValidationErrors: DoorDetailsValidationErrors = {
  doorPlace: "",
  memo: "",
  ap1IpConfigType: "",
  ap2IpConfigType: "",
  ap3IpConfigType: "",
  isLteEnabled: "",
  isWifiEnabled: "",
  ap1StaticIp: "",
  ap2StaticIp: "",
  ap3StaticIp: "",
  sciSignageIpAddr: "",
  sdcSerialNumber: "",
};

interface DoorDetailsTableProps {
  isRegistration: boolean;
  sdc: SdcDetails;
  setSdc: React.Dispatch<React.SetStateAction<SdcDetails>>;
  setFormEdited: React.Dispatch<React.SetStateAction<boolean>>;
  openBuildingModal: boolean;
  setOpenBuildingModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectBuilding: (x: BuildingType) => void;
  handleBlur: (x: string) => void;
  setFetchedTs?: React.Dispatch<React.SetStateAction<number>>;
  validationErrors: DoorDetailsValidationErrors;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  refreshSdcData: () => void;
  // APIから取得した初期値を保持
  initialBillingStartAt?: string | null;
  openConfirmDialog?: boolean;
  setOpenConfirmDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const sdcTxPowerLevelToDisplayStr = (value: string): string => {
  switch (value) {
    case "LOW":
      return "弱";
    case "MIDDLE":
      return "中";
    case "HIGH":
      return "強";
    default:
      return "-";
  }
};

const tableCellChildCheckBox = (
  title: string,
  checked: boolean,
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void,
  textField?: React.ReactNode
) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: tableCellChildHeight,
        ml: tableCellChildMl,
      }}
    >
      <Typography
        sx={{
          width: tableCellChildTitleWidth,
          mr: tableCellChildTitleMr,
        }}
        textAlign={"right"}
      >
        {title}
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={onChange} />}
        label={title === "タッチスイッチ有無" ? "有り" : title === "介助要請モニタ" ? "使用する" : "有効"}
        sx={{
          mr: theme.spacing(5),
        }}
      />
      {textField}
    </Box>
  );
};

const tableCellChildSelectNumber = (
  title: string,
  value: number,
  onChange: (event: SelectChangeEvent<number>, child: React.ReactNode) => void,
  rangeFrom: number,
  rangeTo: number,
  step?: number
) => {
  let step_ = 1;
  if (step) {
    step_ = step;
  }
  const numberArray: number[] = [];
  let work = rangeFrom;
  while (work <= rangeTo) {
    numberArray.push(work);
    work += step_;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: tableCellChildHeight,
        ml: tableCellChildMl,
      }}
    >
      <Typography
        sx={{
          width: tableCellChildTitleWidth,
          mr: tableCellChildTitleMr,
        }}
        textAlign={"right"}
      >
        {title}
      </Typography>
      <FormControl size="small">
        <Select value={value} onChange={onChange}>
          {numberArray.map((v) => {
            let displayStr = String(v);
            if (step_ !== 1) {
              displayStr = v.toFixed(1);
            }
            return (
              <MenuItem value={v} key={v}>
                {displayStr}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export const DoorDetailsTable: React.FC<DoorDetailsTableProps> = (
  props: DoorDetailsTableProps
) => {
  const [openSdcSnSelectionModal, setOpenSdcSnSelectionModal] =
    React.useState<boolean>(false);
  const [simOperationModalOpen, setSimOperationModalOpen] =
    React.useState(false);
  const [selectedOperation, setSelectedOperation] = React.useState<
    "testing" | "waiting_running" | null
  >(null);

  const [showSelectButton, setShowSelectButton] = useState(true);
  const [wasBuildingSelected, setWasBuildingSelected] = useState(false);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  useEffect(() => {
    // 初期状態で物件が選択されている場合はボタンを非表示にする
    if (props.sdc.building !== null) {
      setShowSelectButton(false);
      setWasBuildingSelected(true); // 選択済みとする
    } else {
      setShowSelectButton(true);
      setWasBuildingSelected(false);
    }
  }, []); // 初回のみ実行

  const fetchSdc = async (autoSdcId: number): Promise<void> => {
    const jwtToken = await getJwtTokenFromIdToken();

    try {
      // call showSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/sdcs/" + String(autoSdcId),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );
      props.setFetchedTs!(Date.now());
      const data = (await response.json()) as SdcDetails;
      console.log("SDC（ドア）詳細データ" + JSON.stringify(data));

      props.setSdc({
        ...data,
        // do not overwrite building data
        building: props.sdc.building,
      });
    } catch (error) {
      console.error("Error fetching door data:", error);
    }
  };

  const handleSimOperation = (
    operation: "testing" | "waiting_running" | null
  ) => {
    setSelectedOperation(operation);
    setSimOperationModalOpen(true);
  };

  const renderSimOperationButton = () => {
    if (!props.sdc.simId) {
      return <span>SIM未設定</span>;
    }

    switch (props.sdc.simOperation) {
      case "waiting_operation":
        return (
          <>
            <Button
              variant="contained"
              size="small"
              style={{
                marginRight: "8px",
                backgroundColor: "#f44336", // 赤色 (試験)
                color: "white",
              }}
              onClick={() => handleSimOperation("testing")}
            >
              試験
            </Button>
            <Button
              variant="contained"
              size="small"
              style={{
                backgroundColor: "#4caf50", // 緑色 (運用)
                color: "white",
              }}
              onClick={() => handleSimOperation("waiting_running")}
            >
              運用
            </Button>
          </>
        );
      case "waiting_running":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#2196f3", // 青色 (運用待ち)
              color: "white",
            }}
            onClick={() => handleSimOperation(null)}
          >
            運用待ち
          </Button>
        );
      case "in_running":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#ff9800", // オレンジ色 (運用中)
              color: "black",
            }}
            onClick={() => handleSimOperation(null)}
          >
            運用中
          </Button>
        );
      case "testing":
        return (
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#ffc107", // 黄色 (試験中)
              color: "black",
            }}
            onClick={() => handleSimOperation(null)}
          >
            試験中
          </Button>
        );
      default:
        return <span>操作不可</span>;
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex" }}>
                <Typography>SDCSN</Typography>
                {props.isRegistration && <RequiredLabel />}
              </Box>
            </TableCell>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <Typography>{props.sdc.sdcSerialNumber}</Typography>
                {props.isRegistration && (
                  <Button onClick={() => setOpenSdcSnSelectionModal(true)}>
                    選択してください
                  </Button>
                )}
              </Box>
              {props.isRegistration &&
                props.validationErrors.sdcSerialNumber && (
                  <Typography color="error" variant="body2">
                    {props.validationErrors.sdcSerialNumber}
                  </Typography>
                )}
              {props.isRegistration && (
                <SdcSnSelectionModal
                  open={openSdcSnSelectionModal}
                  onClose={() => {
                    setOpenSdcSnSelectionModal(false);
                  }}
                  onSelect={async (autoSdcId: number) => {
                    await fetchSdc(autoSdcId); // fetchSdc を非同期関数として呼び出す
                    setOpenSdcSnSelectionModal(false);
                  }}
                />
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>SDC出荷日</Typography>
            </TableCell>
            <TableCell align="left">
              <Typography> {formatDate(props.sdc.shippingDate)}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>設置日</Typography>
            </TableCell>
            <TableCell align="left">
              <TextField
                label="設置日"
                type="date"
                value={formatDate(props.sdc.installationDate)}
                size="small"
                onChange={(e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    installationDate: e.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>課金開始日</Typography>
            </TableCell>
            <TableCell align="left">
              <TextField
                label="課金開始日"
                type="date"
                value={formatDate(props.sdc.billingStartAt || "")}
                size="small"
                disabled={
                  !props.isRegistration &&
                  props.initialBillingStartAt !== null &&
                  props.initialBillingStartAt !== undefined &&
                  props.initialBillingStartAt !== ""
                }
                inputProps={{
                  // 日本時間での今日の00:00を基準にする
                  min: new Date(
                    new Date().toLocaleDateString("ja-JP", {
                      timeZone: "Asia/Tokyo",
                    })
                  )
                    .toLocaleDateString("sv")
                    .split(" ")[0],
                }}
                onChange={(e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    billingStartAt: e.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>取付先名（物件名称）</Typography>
            </TableCell>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <Typography>
                  {props.sdc.building !== null &&
                    props.sdc.building.buildingName}
                </Typography>
                {/* 条件を満たす場合のみボタンを表示 */}
                {showSelectButton && (
                  <Button onClick={() => props.setOpenBuildingModal(true)}>
                    物件を選択
                  </Button>
                )}
              </Box>
              {/* BuildingSelectionModalの設定 */}
              <BuildingSelectionModal
                open={props.openBuildingModal}
                onClose={() => props.setOpenBuildingModal(false)}
                // buildings={selectedBuilding}
                handleSelectBuilding={props.handleSelectBuilding}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>物件システムID</Typography>
            </TableCell>
            <TableCell align="left">
              <Typography>
                {props.sdc.building !== null && props.sdc.building.buildingId}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>取付先コード</Typography>
            </TableCell>
            <TableCell align="left">
              <Typography>
                {props.sdc.building !== null &&
                  props.sdc.building.buildingManagementId}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>物件住所</Typography>
            </TableCell>
            <TableCell align="left">
              <Typography>
                {props.sdc.building !== null &&
                  props.sdc.building.buildingAddress}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex" }}>
                <Typography>設置場所</Typography>
                <RequiredLabel />
              </Box>
            </TableCell>
            <TableCell align="left">
              <TextField
                label="設置場所"
                value={props.sdc.doorPlace ?? ""}
                size="small"
                sx={{ width: "600px" }}
                onChange={(e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    doorPlace: e.target.value,
                  }));
                }}
                onBlur={() => props.handleBlur("doorPlace")}
                helperText={props.validationErrors.doorPlace}
                error={props.validationErrors.doorPlace !== ""}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>LTE</Typography>
            </TableCell>
            <TableCell align="left">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.sdc.isLteEnabled}
                    onChange={() => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        isLteEnabled: !s.isLteEnabled,
                      }));
                    }}
                  />
                }
                label="有効"
              />
              <Box display="flex" flexDirection="column" marginTop={2}>
                <Box display="flex" alignItems="center" marginBottom={1}>
                  <Typography variant="body1" marginRight={2}>
                    SIM ID
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {props.sdc.simId || "なし"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" marginRight={2}>
                    SIM状態
                  </Typography>
                  {renderSimOperationButton()}
                </Box>
              </Box>
              <br />
              {/* LTE 詳細設定 */}
              <Accordion
                sx={{
                  boxShadow: "none",
                  margin: 0,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0,
                    minHeight: "auto",
                    "&.Mui-expanded": { minHeight: "auto" },
                  }}
                >
                  <Typography>詳細設定</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0,
                    marginLeft: theme.spacing(4), // 右にずらす（調整可）
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <TextField
                      label="APN"
                      value={props.sdc.lteApn}
                      size="small"
                      onChange={(e) => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          lteApn: e.target.value,
                        }));
                      }}
                    />
                    <TextField
                      label="ID"
                      value={props.sdc.lteId}
                      size="small"
                      onChange={(e) => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          lteId: e.target.value,
                        }));
                      }}
                    />
                    <TextField
                      label="Password"
                      value={props.sdc.ltePassword}
                      size="small"
                      onChange={(e) => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ltePassword: e.target.value,
                        }));
                      }}
                    />
                    {props.validationErrors.isLteEnabled && (
                      <Typography color="error" variant="body2">
                        {props.validationErrors.isLteEnabled}
                      </Typography>
                    )}
                  </Box>

                  <Box display="flex" alignItems="center" marginTop={2}>
                    <Typography marginRight={2}>認証タイプ</Typography>
                    <FormControlLabel
                      value="PAP"
                      control={
                        <Radio
                          checked={props.sdc.lteAuthType === "PAP"}
                          onChange={(e) => {
                            props.setFormEdited(true);
                            props.setSdc((s) => ({
                              ...s,
                              lteAuthType: e.target.value,
                            }));
                          }}
                        />
                      }
                      label="PAP"
                    />
                    <FormControlLabel
                      value="CHAP"
                      control={
                        <Radio
                          checked={props.sdc.lteAuthType === "CHAP"}
                          onChange={(e) => {
                            props.setFormEdited(true);
                            props.setSdc((s) => ({
                              ...s,
                              lteAuthType: e.target.value,
                            }));
                          }}
                        />
                      }
                      label="CHAP"
                    />
                    <FormControlLabel
                      value="NONE"
                      control={
                        <Radio
                          checked={props.sdc.lteAuthType === "NONE"}
                          onChange={(e) => {
                            props.setFormEdited(true);
                            props.setSdc((s) => ({
                              ...s,
                              lteAuthType: e.target.value,
                            }));
                          }}
                        />
                      }
                      label="NONE"
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>Wi-Fi</Typography>
            </TableCell>
            <TableCell align="left">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={props.sdc.isWifiEnabled}
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          isWifiEnabled: !s.isWifiEnabled,
                        }));
                      }}
                    />
                  }
                  label="Wi-Fi有効"
                />
                <Typography sx={{ ml: theme.spacing(8) }}>
                  優先接続AP：
                </Typography>
                <FormControl size="small">
                  <Select
                    value={props.sdc.defaultWifi}
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        defaultWifi: Number(e.target.value),
                      }));
                    }}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {props.validationErrors.isWifiEnabled && (
                <Typography color="error" variant="body2">
                  {props.validationErrors.isWifiEnabled}
                </Typography>
              )}
              <br />

              {/* AP設定 */}
              <Accordion
                sx={{
                  boxShadow: "none",
                  margin: 0,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0,
                    minHeight: "auto",
                    "&.Mui-expanded": { minHeight: "auto" },
                  }}
                >
                  <Typography>アクセスポイント（AP）設定</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0,
                    marginLeft: theme.spacing(4), // 右にずらす（調整可）
                  }}
                >
                  {/* AP1 設定 */}
                  <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
                    AP1 設定
                  </Typography>
                  <TextField
                    label="AP1 SSID"
                    value={props.sdc.ap1Ssid}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({ ...s, ap1Ssid: e.target.value }));
                    }}
                  />
                  <TextField
                    label="AP1 PSK"
                    value={props.sdc.ap1Password}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        ap1Password: e.target.value,
                      }));
                    }}
                  />
                  <Box display="flex" alignItems="center" marginTop={1}>
                    <FormControlLabel
                      value="DHCP"
                      control={
                        <Radio checked={props.sdc.ap1IpConfigType === "DHCP"} />
                      }
                      label="DHCP"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap1IpConfigType: "DHCP",
                        }));
                      }}
                    />
                    <FormControlLabel
                      value="STATIC"
                      control={
                        <Radio
                          checked={props.sdc.ap1IpConfigType === "STATIC"}
                        />
                      }
                      label="STATIC"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap1IpConfigType: "STATIC",
                        }));
                      }}
                    />
                  </Box>
                  {props.sdc.ap1IpConfigType === "STATIC" && (
                    <>
                      <TextField
                        label="IP"
                        value={props.sdc.ap1StaticIp ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap1StaticIp: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="GW"
                        value={props.sdc.ap1StaticGateWay ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap1StaticGateWay: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="DNS"
                        value={props.sdc.ap1StaticDns ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap1StaticDns: e.target.value,
                          }));
                        }}
                      />
                    </>
                  )}

                  {/* AP2 設定 */}
                  <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
                    AP2 設定
                  </Typography>
                  <TextField
                    label="AP2 SSID"
                    value={props.sdc.ap2Ssid}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({ ...s, ap2Ssid: e.target.value }));
                    }}
                  />
                  <TextField
                    label="AP2 PSK"
                    value={props.sdc.ap2Password}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        ap2Password: e.target.value,
                      }));
                    }}
                  />
                  <Box display="flex" alignItems="center" marginTop={1}>
                    <FormControlLabel
                      value="DHCP"
                      control={
                        <Radio checked={props.sdc.ap2IpConfigType === "DHCP"} />
                      }
                      label="DHCP"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap2IpConfigType: "DHCP",
                        }));
                      }}
                    />
                    <FormControlLabel
                      value="STATIC"
                      control={
                        <Radio
                          checked={props.sdc.ap2IpConfigType === "STATIC"}
                        />
                      }
                      label="STATIC"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap2IpConfigType: "STATIC",
                        }));
                      }}
                    />
                  </Box>
                  {props.sdc.ap2IpConfigType === "STATIC" && (
                    <>
                      <TextField
                        label="IP"
                        value={props.sdc.ap2StaticIp ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap2StaticIp: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="GW"
                        value={props.sdc.ap2StaticGateWay ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap2StaticGateWay: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="DNS"
                        value={props.sdc.ap2StaticDns ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap2StaticDns: e.target.value,
                          }));
                        }}
                      />
                    </>
                  )}

                  {/* AP3 設定 */}
                  <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
                    AP3 設定
                  </Typography>
                  <TextField
                    label="AP3 SSID"
                    value={props.sdc.ap3Ssid}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({ ...s, ap3Ssid: e.target.value }));
                    }}
                  />
                  <TextField
                    label="AP3 PSK"
                    value={props.sdc.ap3Password}
                    size="small"
                    onChange={(e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        ap3Password: e.target.value,
                      }));
                    }}
                  />
                  <Box display="flex" alignItems="center" marginTop={1}>
                    <FormControlLabel
                      value="DHCP"
                      control={
                        <Radio checked={props.sdc.ap3IpConfigType === "DHCP"} />
                      }
                      label="DHCP"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap3IpConfigType: "DHCP",
                        }));
                      }}
                    />
                    <FormControlLabel
                      value="STATIC"
                      control={
                        <Radio
                          checked={props.sdc.ap3IpConfigType === "STATIC"}
                        />
                      }
                      label="STATIC"
                      onChange={() => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          ap3IpConfigType: "STATIC",
                        }));
                      }}
                    />
                  </Box>
                  {props.sdc.ap3IpConfigType === "STATIC" && (
                    <>
                      <TextField
                        label="IP"
                        value={props.sdc.ap3StaticIp ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap3StaticIp: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="GW"
                        value={props.sdc.ap3StaticGateWay ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap3StaticGateWay: e.target.value,
                          }));
                        }}
                      />
                      <TextField
                        label="DNS"
                        value={props.sdc.ap3StaticDns ?? ""}
                        size="small"
                        onChange={(e) => {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            ap3StaticDns: e.target.value,
                          }));
                        }}
                      />
                    </>
                  )}
                </AccordionDetails>
                <br />
              </Accordion>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>FTスマートキー機能</Typography>
            </TableCell>
            <TableCell align="left">
              {/* FTスマートキー関連 */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.sdc.isAalEnabled}
                    onChange={(event) => {
                      if (props.isRegistration) {
                        // 登録ページでは確認ダイアログを表示せず、直接値を更新
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          isAalEnabled: event.target.checked,
                        }));
                      } else {
                        // 編集ページの場合は確認ダイアログを表示
                        if (props.sdc.isAalEnabled && !event.target.checked) {
                          if (props.setOpenConfirmDialog) {
                            props.setOpenConfirmDialog(true);
                          }
                        } else {
                          props.setFormEdited(true);
                          props.setSdc((s) => ({
                            ...s,
                            isAalEnabled: event.target.checked,
                          }));
                        }
                      }
                    }}
                  />
                }
                label="有効"
              />
              ※FTスマートキーアプリに対応します。
              <br />
              {/* 詳細設定 */}
              <Accordion
                sx={{
                  boxShadow: "none",
                  margin: 0,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0,
                    minHeight: "auto",
                    "&.Mui-expanded": { minHeight: "auto" },
                  }}
                >
                  <Typography>詳細設定</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {tableCellChildCheckBox(
                    "NFC",
                    props.sdc.isNfcEnabled,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        isNfcEnabled: !s.isNfcEnabled,
                      }));
                    }
                  )}
                  {tableCellChildCheckBox(
                    "セキュリティ強化",
                    props.sdc.isNfcCertRequired,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        isNfcCertRequired: !s.isNfcCertRequired,
                      }));
                    }
                  )}
                  {tableCellChildSelectNumber(
                    "接近距離（m）",
                    props.sdc.defaultDistanceNear,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        defaultDistanceNear: Number(e.target.value),
                      }));
                    },
                    1,
                    15,
                    0.5
                  )}
                  {tableCellChildSelectNumber(
                    "離遠距離（m）",
                    props.sdc.defaultDistanceFar,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        defaultDistanceFar: Number(e.target.value),
                      }));
                    },
                    1,
                    15,
                    0.5
                  )}
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>ミライロドア機能</Typography>
            </TableCell>
            <TableCell align="left">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.sdc.isSciEnabled}
                    onChange={() => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        isSciEnabled: !s.isSciEnabled,
                      }));
                    }}
                  />
                }
                label="有効"
              />
              ※ミライロドアアプリに対応します。
              <br />
              {tableCellChildCheckBox(
                "スピーカ設置",
                props.sdc.isGuideBellEnabled,
                (e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    isGuideBellEnabled: !s.isGuideBellEnabled,
                  }));
                }
              )}
              {tableCellChildCheckBox(
                "サイネージ",
                props.sdc.isSignageEnabled,
                (e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    isSignageEnabled: !s.isSignageEnabled,
                  }));
                },
                <TextField
                  label="IPアドレス"
                  value={props.sdc.sciSignageIpAddr}
                  size="small"
                  sx={{ width: theme.spacing(10) }}
                  onChange={(e) => {
                    props.setFormEdited(true);
                    props.setSdc((s) => ({
                      ...s,
                      sciSignageIpAddr: e.target.value,
                    }));
                  }}
                  onBlur={() => props.handleBlur("sciSignageIpAddr")}
                  helperText={props.validationErrors.sciSignageIpAddr}
                  error={props.validationErrors.sciSignageIpAddr !== ""}
                />
              )}
              {tableCellChildCheckBox(
                "介助要請モニタ",
                props.sdc.isSpeakerEnabled,
                (e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    isSpeakerEnabled: !s.isSpeakerEnabled,
                  }));
                }
              )}
              <Accordion
                sx={{
                  boxShadow: "none", // シャドウを削除
                  margin: 0, // マージンを削除
                  "&:before": {
                    display: "none", // デフォルトの境界線を削除
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0, // パディングを削除
                    minHeight: "auto", // 高さ調整
                    "&.Mui-expanded": {
                      minHeight: "auto", // 展開時の高さを固定
                    },
                  }}
                >
                  <Typography>その他設定</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0, // パディングを削除
                  }}
                >
                  {tableCellChildCheckBox(
                    "介助要請接点出力",
                    props.sdc.isSpeakerContactEnabled,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        isSpeakerContactEnabled: !s.isSpeakerContactEnabled,
                      }));
                    }
                  )}
                  {tableCellChildSelectNumber(
                    "SDC（ドア）区別番号",
                    props.sdc.idInCluster,
                    (e) => {
                      props.setFormEdited(true);
                      props.setSdc((s) => ({
                        ...s,
                        idInCluster: Number(e.target.value),
                      }));
                    },
                    0,
                    7
                  )}
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography>SDC（ドア）共通機能</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              {/* タッチスイッチ */}
              {/* {tableCellChildCheckBox(
                "タッチスイッチ有無",
                props.sdc.isPushButtonEnabled,
                (e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    isPushButtonEnabled: !s.isPushButtonEnabled,
                  }));
                }
              )} */}
              <Accordion
                sx={{
                  boxShadow: "none", // シャドウを削除
                  margin: 0, // マージンを削除
                  "&:before": {
                    display: "none", // デフォルトの境界線を削除
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    padding: 0, // パディングを削除
                    minHeight: "auto", // 高さ調整
                    "&.Mui-expanded": {
                      minHeight: "auto", // 展開時の高さを固定
                    },
                  }}
                >
                  <Typography></Typography>  {/* タイトルをあえて入れないことによって左の文字がこのアコーディオンのタイトルに見えるようになる */}
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0, // パディングを削除
                  }}
                >
                  <Box>
                    {/* デフォルト半開にする */}
                    {tableCellChildCheckBox(
                      "デフォルト半開にする",
                      props.sdc.isDefaultOpenHalf,
                      (e) => {
                        props.setFormEdited(true);
                        props.setSdc((s) => ({
                          ...s,
                          isDefaultOpenHalf: !s.isDefaultOpenHalf,
                        }));
                      }
                    )}

                    {/* ビーコン電波強度 */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: tableCellChildHeight,
                        ml: tableCellChildMl,
                      }}
                    >
                      <Typography
                        sx={{
                          width: tableCellChildTitleWidth,
                          mr: tableCellChildTitleMr,
                        }}
                        textAlign={"right"}
                      >
                        {"ビーコン電波強度"}
                      </Typography>
                      <FormControl size="small">
                        <Select
                          value={props.sdc.txPowerLevel}
                          onChange={(e) => {
                            props.setFormEdited(true);
                            props.setSdc((s) => ({
                              ...s,
                              txPowerLevel: e.target.value,
                            }));
                          }}
                        >
                          {sdcTxPowerLevels.map((v) => {
                            return (
                              <MenuItem value={v} key={v}>
                                {sdcTxPowerLevelToDisplayStr(v)}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Minor値 */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: tableCellChildHeight,
                        ml: tableCellChildMl,
                      }}
                    >
                      <Typography
                        sx={{
                          width: tableCellChildTitleWidth,
                          mr: tableCellChildTitleMr,
                        }}
                        textAlign={"right"}
                      >
                        Minor値
                        {<AutomaticallyCalculatedLabel />}
                      </Typography>
                      <Typography fontFamily="monospace">
                        {new HaudiUuid(
                          props.sdc.sdcUuid,
                          props.sdc.isAalEnabled,
                          props.sdc.isSciEnabled,
                          {
                            isGuideBellEnabled: props.sdc.isGuideBellEnabled,
                            isSignageEnabled: props.sdc.isSignageEnabled,
                            isSpeakerEnabled: props.sdc.isSpeakerEnabled,
                            idInCluster: props.sdc.idInCluster,
                          }
                        )
                          .getMinorValue()
                          .toString("hex")
                          .padStart(2, "0")}
                      </Typography>
                    </Box>
                    {/* ログ頻度 */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: tableCellChildHeight,
                        ml: tableCellChildMl,
                      }}
                    >
                      <Typography
                        sx={{
                          width: tableCellChildTitleWidth,
                          mr: tableCellChildTitleMr,
                        }}
                        textAlign={"right"}
                      >
                        ログ頻度
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          row
                          value={props.sdc.logMode ?? "0"}
                          onChange={(e) => {
                            props.setFormEdited(true);
                            props.setSdc((s) => ({
                              ...s,
                              logMode: e.target.value, // 新しいlogModeフィールド
                            }));
                          }}
                        >
                          <FormControlLabel
                            value="0"
                            control={<Radio />}
                            label="通常"
                          />
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="詳細"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography>メモ</Typography>
            </TableCell>
            <TableCell align="left">
              <TextField
                label="メモ"
                value={props.sdc.memo}
                size="small"
                sx={{ width: "600px" }}
                onChange={(e) => {
                  props.setFormEdited(true);
                  props.setSdc((s) => ({
                    ...s,
                    memo: e.target.value,
                  }));
                }}
                onBlur={() => props.handleBlur("memo")}
                helperText={props.validationErrors.memo}
                error={props.validationErrors.memo !== ""}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {/* SimOperationModal を追加 */}
      <SimOperationModal
        open={simOperationModalOpen}
        onClose={() => {
          setSimOperationModalOpen(false);
          setSelectedOperation(null);
        }}
        autoSdcId={props.sdc.autoSdcId}
        simId={props.sdc.simId || ""}
        currentOperation={props.sdc.simOperation || ""}
        selectedOperation={selectedOperation}
        onSuccess={(message) => {
          props.onSuccess(message);
          props.refreshSdcData();
        }}
        onError={props.onError}
      />
    </TableContainer>
  );
};
